import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, X as XIcon, Check, ArrowRight, Loader2, Sparkles } from "lucide-react";
import client from "../lib/api";

/**
 * Weekly pulse. Two fast one-to-five questions, once a week, per
 * product. A gentle Home nudge opens the inline picker; a dedicated
 * screen shows the same picker plus the trendline over time. Nothing
 * is scored and nothing leaves the owner.
 */

const SEG_COLORS = ["hsl(28 74% 55%)", "hsl(38 74% 52%)", "hsl(46 62% 48%)", "hsl(150 40% 44%)", "hsl(var(--klareet-teal))"];

function PulseScale({ q, index, value, onPick }) {
  return (
    <div className="mb-5" data-testid={`weekly-pulse-q${index}`}>
      <div className="text-sm font-medium mb-2" style={{ color: "hsl(var(--klareet-ink))" }}>{q.label}</div>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((v) => {
          const on = value === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onPick(v)}
              data-testid={`weekly-pulse-q${index}-seg-${v}`}
              aria-pressed={on}
              className="flex-1 h-10 rounded-lg text-sm font-semibold border transition-all duration-200 hover:-translate-y-0.5"
              style={{
                background: on ? SEG_COLORS[v - 1] : "hsl(var(--klareet-canvas-2))",
                color: on ? "#fff" : "hsl(var(--klareet-ink-soft))",
                borderColor: on ? SEG_COLORS[v - 1] : "hsl(var(--klareet-line-soft))",
              }}
            >
              {v}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="k-quiet !text-[11px]">{q.low}</span>
        <span className="k-quiet !text-[11px]">{q.high}</span>
      </div>
    </div>
  );
}

export function PulsePicker({ questions, product, onSaved, compact }) {
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [busy, setBusy] = useState(false);
  const [klair, setKlair] = useState("");
  const [shift, setShift] = useState("");
  const ready = q1 != null && q2 != null;

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const { data } = await client.post("/engagement/weekly-checkin", { product, q1, q2 });
      setKlair(data?.klair || "");
      setShift(data?.shift?.line || "");
      if (onSaved) onSaved(data);
    } catch (_e) { /* silent */ }
    finally { setBusy(false); }
  };

  if (klair) {
    return (
      <div data-testid="weekly-pulse-klair">
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: "hsl(var(--klareet-teal) / 0.08)", border: "1px solid hsl(var(--klareet-teal) / 0.24)" }}>
          <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "hsl(var(--klareet-teal))", color: "#fff" }}><Check size={16} /></span>
          <div>
            <div className="k-label mb-1">logged, thank you</div>
            <p className="klareet-serif" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5, fontSize: "1.02rem" }}>{klair}</p>
          </div>
        </div>
        {shift && (
          <div className="rounded-xl p-4 mt-3 flex items-start gap-3" style={{ background: "hsl(255 50% 96%)", border: "1px solid hsl(255 38% 88%)" }} data-testid="weekly-pulse-shift">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "hsl(255 45% 55%)", color: "#fff" }}><Sparkles size={15} /></span>
            <div>
              <div className="k-label mb-1">something worth noticing</div>
              <p className="klareet-serif" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5, fontSize: "1.02rem" }}>{shift}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="weekly-pulse-picker">
      {(questions || []).map((q, i) => (
        <PulseScale key={q.key} q={q} index={i} value={i === 0 ? q1 : q2} onPick={(v) => (i === 0 ? setQ1(v) : setQ2(v))} />
      ))}
      <button onClick={submit} disabled={!ready || busy} className="k-btn k-btn-primary !text-sm" data-testid="weekly-pulse-submit">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Saving.</> : <><Sparkles size={14} /> {compact ? "Save my pulse" : "Log this week"}</>}
      </button>
    </div>
  );
}

export function PulseTrend({ history, questions }) {
  if (!history || history.length < 2) {
    return (
      <p className="k-quiet !text-sm" data-testid="weekly-pulse-trend-empty">
        Your trendline draws itself once you have logged two weeks. Keep it up.
      </p>
    );
  }
  const W = 560, H = 150, padX = 16, padY = 16;
  const n = history.length;
  const x = (i) => padX + (i * (W - padX * 2)) / Math.max(1, n - 1);
  const y = (v) => padY + ((5 - v) * (H - padY * 2)) / 4;
  const line = (key) => history.map((h, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(h[key] ?? 3).toFixed(1)}`).join(" ");
  const series = [
    { key: "q1", color: "hsl(var(--klareet-teal))", label: questions?.[0]?.key || "one" },
    { key: "q2", color: "hsl(28 74% 52%)", label: questions?.[1]?.key || "two" },
  ];
  return (
    <div data-testid="weekly-pulse-trend">
      <div className="flex gap-4 mb-2">
        {series.map((s) => (
          <span key={s.key} className="k-quiet !text-xs flex items-center gap-1.5">
            <span className="inline-block w-3 h-1.5 rounded-full" style={{ background: s.color }} /> {s.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "auto" }} preserveAspectRatio="none" role="img" aria-label="weekly pulse trend">
        {[1, 2, 3, 4, 5].map((v) => (
          <line key={v} x1={padX} x2={W - padX} y1={y(v)} y2={y(v)} stroke="hsl(var(--klareet-line-soft))" strokeWidth="1" />
        ))}
        {series.map((s) => (
          <g key={s.key}>
            <path d={line(s.key)} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
            {history.map((h, i) => (
              <circle key={i} cx={x(i)} cy={y(h[s.key] ?? 3)} r="3.5" fill={s.color} />
            ))}
          </g>
        ))}
      </svg>
      <div className="flex justify-between mt-1">
        {history.map((h, i) => (
          <span key={i} className="k-quiet !text-[10px]" style={{ transform: "translateX(-2px)" }}>{shortWeek(h.week)}</span>
        ))}
      </div>
    </div>
  );
}

function shortWeek(week) {
  if (!week) return "";
  const m = String(week).match(/W(\d+)/);
  return m ? `w${Number(m[1])}` : week;
}

/** The Home dashboard nudge. When due, opens an inline modal. When
 *  already logged this week, shows a compact done chip with a link to
 *  the full trend screen. */
export function WeeklyPulseCard({ product, base }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [savedThisSession, setSavedThisSession] = useState(false);

  const load = () => {
    client.get(`/engagement/weekly-checkin?product=${product}`)
      .then((r) => setState(r.data || null))
      .catch(() => setState(null));
  };
  useEffect(() => { load(); }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state) return null;
  const due = state.due && !savedThisSession;

  if (!due) {
    return (
      <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }} data-testid="weekly-pulse-done">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(var(--klareet-teal) / 0.14)", color: "hsl(var(--klareet-teal))" }}><Check size={17} /></span>
        <div className="min-w-0 flex-1">
          <div className="k-label mb-0.5">weekly pulse</div>
          <div className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>Logged for this week. Nice and steady.</div>
        </div>
        <Link to={`${base}/pulse`} className="k-btn k-btn-ghost !py-1.5 !px-3 !text-xs shrink-0" data-testid="weekly-pulse-view-trend">
          See the trend <ArrowRight size={12} />
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl p-5 flex items-start gap-3 k-fade-in" style={{ background: "linear-gradient(135deg, hsl(28 80% 96%), hsl(var(--klareet-canvas-2)))", border: "1px solid hsl(28 70% 84%)" }} data-testid="weekly-pulse-card">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(28 74% 55% / 0.16)", color: "hsl(28 74% 45%)" }}><Activity size={17} /></span>
        <div className="min-w-0 flex-1">
          <div className="k-label mb-1">your weekly pulse</div>
          <p className="klareet-serif" style={{ fontSize: "1.1rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.4 }}>
            Two quick taps on how this week went. It builds your snapshot over time.
          </p>
          <button onClick={() => setOpen(true)} className="k-btn k-btn-primary !text-sm mt-3" data-testid="weekly-pulse-open">
            Take it, 20 seconds <ArrowRight size={13} />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: "hsl(var(--klareet-ink) / 0.45)", backdropFilter: "blur(3px)" }} data-testid="weekly-pulse-modal" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl p-6 k-fade-in" style={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--klareet-line))", boxShadow: "0 20px 60px hsl(var(--klareet-ink) / 0.25)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="k-label mb-1 flex items-center gap-2"><Activity size={13} /> weekly pulse</div>
                <h3 className="klareet-serif" style={{ fontSize: "1.3rem", color: "hsl(var(--klareet-ink))" }}>How did this week go?</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full opacity-60 hover:opacity-100" aria-label="close" data-testid="weekly-pulse-close" style={{ color: "hsl(var(--klareet-muted))" }}><XIcon size={16} /></button>
            </div>
            <PulsePicker
              questions={state.questions}
              product={product}
              compact
              onSaved={() => { setSavedThisSession(true); setTimeout(() => { setOpen(false); load(); }, 1600); }}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default WeeklyPulseCard;
